import { inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { switchMap } from "rxjs/operators";

import { ExportFormatterService } from "../export-formatter.service";
import { SearchParametersQuery, SearchResult, SearchResultItem } from "../../../model";
import { SEARCH_API_SERVICE } from "./search-api.token";
import { ViewerConfigurationService } from "../../../../viewer-components/services/viewer-configuration.service";
import { GeoService } from "../../../../viewer-components/services/internal-services/geo.service";

/**
 * High-level search service that provides the public API for search operations
 * Uses the configured search API implementation via injection token
 */
@Injectable({
  providedIn: "root",
})
export class SearchRequestService {
  searchApiService = inject(SEARCH_API_SERVICE);

  exportFormatterService = inject(ExportFormatterService);

  private viewerConfigurationService = inject(ViewerConfigurationService);

  /**
   * Performs a search query and post-processes the results (e.g., centroids, normalization)
   * @param params Search parameters in final API format
   * @returns Observable containing the processed search results
   */
  search(params: SearchParametersQuery): Observable<SearchResult> {
    return this.searchApiService
      .search(params)
      .pipe(switchMap((results: SearchResult) => this.processResults(results)));
  }

  /**
   * Checks whether a geometry filter (wkt/buffer) matches at least one compound,
   * without fetching/mapping full search results. Used e.g. to validate a
   * geofencing filter before applying it.
   * @param wkt WKT geometry to validate
   * @param bufferMeter Optional buffer radius in meters
   * @returns Observable resolving to true if at least one result matches
   */
  validateGeometry(wkt: string, bufferMeter?: number): Observable<boolean> {
    const implementation = this.viewerConfigurationService.getEmbeddingsImplementation();
    const params: SearchParametersQuery = {
      wkt,
      ...(bufferMeter !== undefined ? { "buffer-meter": bufferMeter } : {}),
      ...(implementation ? { implementation } : {}),
    };
    return this.searchApiService.validateGeometry(params);
  }

  /**
   * Export search results with smart reuse of already loaded data
   * @param loadedResults Already loaded search results
   * @param searchFn Function to fetch additional results if needed
   * @param params Original search parameters
   * @param format Export format (csv, geojson-polygon, geojson-point)
   * @param maxResults Maximum number of results to export
   * @param progressCallback Callback to report progress updates
   * @param updateStateCallback Callback to update search state with additional results and pagination info
   * @returns Observable containing exported data
   */
  export(
    loadedResults: SearchResultItem[],
    searchFn: (params: SearchParametersQuery) => Observable<SearchResult>,
    params: SearchParametersQuery,
    format: "csv" | "geojson-polygon" | "geojson-point",
    maxResults: number = 1000,
    progressCallback?: (downloaded: number, total: number) => void,
    updateStateCallback?: (additionalResults: SearchResultItem[], lastPageLoaded: number) => void,
  ): Observable<any> {
    return this.exportFormatterService.exportResults(
      loadedResults,
      searchFn,
      params,
      format,
      maxResults,
      progressCallback,
      updateStateCallback,
    );
  }

  /**
   * Post-process search results (e.g., calculate centroids, normalization)
   * @param results Raw search results
   * @returns Observable containing processed results
   */
  processResults(results: SearchResult): Observable<SearchResult> {
    return new Observable<SearchResult>((subscriber) => {
      try {
        const processedItems: SearchResultItem[] = results.items.map((item) => ({
          ...item,
          // Add any post-processing logic here
          // For example: calculate centroids from geometry
          collection: item?.collection || null,
          centroid: GeoService.wktPolygonCentroid(item.geometry),
        }));

        const processedResults: SearchResult = {
          ...results,
          items: processedItems,
          metadata: {
            ...results.metadata,
            processed: true,
            processedAt: new Date().toISOString(),
          },
        };

        subscriber.next(processedResults);
        subscriber.complete();
      } catch (error) {
        subscriber.error(error);
      }
    });
  }
}
